import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { grey } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import GroupIcon from "@material-ui/icons/Group";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import FinalizacaoVendaModal from "../FinalizacaoVendaModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { Done, HighlightOff, SwapHoriz, Add } from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import NewTicketModal from "../NewTicketModal";
import {
  Avatar,
  ListItem,
  Typography,
  Dialog,
  DialogActions,
  Button,
  DialogContent,
} from "@material-ui/core";

// ─── Pill helpers ──────────────────────────────────────────────────────────────

/** Conexão / fila / atendente: fundo sólido, texto branco, bem pequeno */
const MetaPill = ({ color, children, style = {} }) => (
  <span
    style={{
      backgroundColor: color || "#9e9e9e",
      color: "#fff",
      fontSize: 9,
      fontWeight: 700,
      padding: "1px 6px",
      borderRadius: 8,
      textTransform: "uppercase",
      letterSpacing: "0.3px",
      lineHeight: "14px",
      display: "inline-block",
      whiteSpace: "nowrap",
      ...style,
    }}
  >
    {children}
  </span>
);

/** Etapa do Kanban: fundo sólido, destaque ligeiramente maior */
const StagePill = ({ color, children }) => (
  <span
    style={{
      backgroundColor: color || "#9e9e9e",
      color: "#fff",
      fontSize: 10,
      fontWeight: 700,
      padding: "2px 7px",
      borderRadius: 10,
      textTransform: "uppercase",
      letterSpacing: "0.4px",
      lineHeight: "14px",
      display: "inline-block",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

/** Tag de origem (kanban=0): fundo translúcido, borda colorida */
const TagPill = ({ color, children }) => {
  const hex = color || "#888888";
  return (
    <span
      style={{
        backgroundColor: `${hex}1a`,
        color: hex,
        border: `1px solid ${hex}`,
        fontSize: 10,
        fontWeight: 600,
        padding: "1px 6px",
        borderRadius: 10,
        lineHeight: "14px",
        display: "inline-block",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
};

/** Botão de ação: ícone colorido com tooltip */
const ActionBtn = ({ color, tooltip, onClick, children, loading }) => (
  <Tooltip title={tooltip || ""}>
    <span
      onClick={loading ? undefined : onClick}
      style={{
        color: loading ? "#ccc" : color,
        cursor: loading ? "default" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 3,
        borderRadius: "50%",
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => {
        if (!loading) e.currentTarget.style.background = "rgba(0,0,0,0.07)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {children}
    </span>
  </Tooltip>
);

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    alignItems: "flex-start",
    padding: "10px 10px 8px 10px",
    borderBottom: `1px solid ${
      theme.mode === "light" ? "#eeeeee" : "rgba(255,255,255,0.07)"
    }`,
    "&:hover": {
      backgroundColor:
        theme.mode === "light" ? "#f9f9f9" : "rgba(255,255,255,0.04)",
    },
    cursor: "pointer",
  },
  pendingTicket: {
    cursor: "unset",
  },
  clickableAvatar: {
    cursor: "pointer",
    "&:hover": { opacity: 0.85 },
  },
  // modal image
  expandedImage: {
    width: "100%",
    height: "auto",
    maxWidth: 500,
    borderRadius: theme.spacing(1),
  },
}));

// ─── Component ────────────────────────────────────────────────────────────────

const TicketListItemCustom = ({ setTabOpen, ticket }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");
  const [openFinalizacaoVenda, setOpenFinalizacaoVenda] = useState(false);
  const [finalizacaoTipo, setFinalizacaoTipo] = useState(null);
  const [ticketDataToFinalize, setTicketDataToFinalize] = useState(null);
  const [showFinalizacaoOptions, setShowFinalizacaoOptions] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  const { get: getSetting } = useCompanySettings();

  useEffect(() => () => { isMounted.current = false; }, []);

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (ticket?.contact?.urlPicture) setImageModalOpen(true);
  };

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
    setAcceptTicketWithouSelectQueueOpen(true);
  }, []);

  const handleCloseTicket = async (id) => {
    if (user.finalizacaoComValorVendaAtiva === true || user.finalizacaoComValorVendaAtiva === "true") {
      setFinalizacaoTipo("comDespedida");
      setOpenFinalizacaoVenda(true);
      handleSelectTicket(ticket);
      history.push(`/tickets/${ticket.uuid}`);
    } else {
      const setting = await getSetting({ column: "requiredTag" });
      if (setting.requiredTag === "enabled") {
        try {
          const contactTags = await api.get(`/contactTags/${ticket.contact.id}`);
          if (!contactTags.data.tags) {
            toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
          } else {
            await api.put(`/tickets/${id}`, { status: "closed", userId: user?.id || null });
            if (isMounted.current) setLoading(false);
            history.push(`/tickets/`);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      } else {
        setLoading(true);
        try {
          await api.put(`/tickets/${id}`, { status: "closed", userId: user?.id || null });
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
        if (isMounted.current) setLoading(false);
        history.push(`/tickets/`);
      }
    }
  };

  const handleCloseIgnoreTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) setLoading(false);
    history.push(`/tickets/`);
  };

  const truncate = (str, len) => {
    if (!isNil(str)) {
      if (str.length > len) return str.substring(0, len) + "...";
      return str;
    }
  };

  const handleCloseTransferTicketModal = useCallback(() => {
    if (isMounted.current) setTransferTicketModalOpen(false);
  }, []);

  const handleOpenTransferModal = () => {
    setLoading(true);
    setTransferTicketModalOpen(true);
    if (isMounted.current) setLoading(false);
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleOpenNewTicketModal = () => setNewTicketModalOpen(true);

  const handleCloseNewTicketModal = (newTicket) => {
    setNewTicketModalOpen(false);
    if (newTicket) {
      handleSelectTicket(newTicket);
      history.push(`/tickets/${newTicket.uuid}`);
    }
  };

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      const otherTicket = await api.put(`/tickets/${id}`, {
        status: ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
        userId: user?.id,
      });
      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);
          setTabOpen(ticket.isGroup ? "group" : "open");
          handleSelectTicket(otherTicket.data);
          history.push(`/tickets/${otherTicket.uuid}`);
        }
      } else {
        let setting;
        try {
          setting = await getSetting({ column: "sendGreetingAccepted" });
        } catch (err) {
          toastError(err);
        }
        if (setting.sendGreetingAccepted === "enabled" && (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")) {
          handleSendMessage(ticket.id);
        }
        if (isMounted.current) setLoading(false);
        setTabOpen(ticket.isGroup ? "group" : "open");
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSendMessage = async (id) => {
    let setting;
    try {
      setting = await getSetting({ column: "greetingAcceptedMessage" });
    } catch (err) {
      toastError(err);
    }
    if (!setting.greetingAcceptedMessage) {
      toast.warning(i18n.t("messagesList.header.buttons.greetingAcceptedMessage"));
      return;
    }
    try {
      await api.post(`/messages/${id}`, {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body: setting.greetingAcceptedMessage.trim(),
      });
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseAlert = useCallback(() => {
    setOpenAlert(false);
    setLoading(false);
  }, []);

  const handleSelectTicket = (t) => {
    const code = uuidv4();
    const { id, uuid } = t;
    setCurrentTicket({ id, uuid, code });
  };

  const handleUpdateTicketStatusWithData = async (ticketData, sendFarewellMessage, finalizacaoMessage) => {
    try {
      await api.put(`/tickets/${ticket.id}`, { ...ticketData, sendFarewellMessage, finalizacaoMessage });
      toast.success("Ticket finalizado com sucesso!");
      history.push(`/tickets/`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSpyTicket = () => {
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  // ─── Derived values ──────────────────────────────────────────────────────────

  const shouldBlurMessages =
    ticket.status === "pending" && user?.allowSeeMessagesInPendingTickets === "disabled";

  const renderLastMessage = () => {
    if (shouldBlurMessages) return <MarkdownWrapper>{i18n.t("tickets.messageHidden") || "Mensagem oculta"}</MarkdownWrapper>;
    if (!ticket.lastMessage) return null;
    if (ticket.lastMessage.includes("data:image/png;base64")) return <MarkdownWrapper>Localização</MarkdownWrapper>;
    if (ticket.lastMessage.includes("BEGIN:VCARD")) return <MarkdownWrapper>Contato</MarkdownWrapper>;
    return <MarkdownWrapper>{truncate(ticket.lastMessage, 50)}</MarkdownWrapper>;
  };

  // Tags: ticket.tags (kanban=1 = etapas; kanban=0 = tags de ticket)
  //       contact.tags (tags do contato não duplicadas)
  const ticketTags = ticket?.tags || [];
  const ticketTagIds = new Set(ticketTags.map((t) => t.id));
  const contactTags = (ticket?.contact?.tags || []).filter(
    (t) => !t.kanban && !ticketTagIds.has(t.id)
  );
  const allTags = [...ticketTags, ...contactTags];
  const stageTags = allTags.filter((t) => t.kanban === 1);
  const originTags = allTags.filter((t) => !t.kanban);

  // Contact display name (ignora LID numérico)
  const isLid = (val) => val && /^\d{14,}$/.test(val);
  const { name: cName, number: cNumber } = ticket.contact || {};
  const contactDisplayName = (() => {
    if (cName && !isLid(cName)) return truncate(cName, 45);
    if (cNumber && !isLid(cNumber)) return truncate(cNumber, 45);
    return truncate(cName || cNumber || "", 45);
  })();

  const unread = Number(ticket.unreadMessages);
  const isDark = theme.mode !== "light";

  // Connection color
  const connColor =
    ticket.channel === "whatsapp"
      ? ticket.whatsapp?.color || "#25D366"
      : ticket.channel === "facebook"
      ? "#4267B2"
      : "#E1306C";

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <React.Fragment key={ticket.id}>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={() => setAcceptTicketWithouSelectQueueOpen(false)}
          ticketId={ticket.id}
          ticket={ticket}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}
      {newTicketModalOpen && (
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          onClose={handleCloseNewTicketModal}
          initialContact={ticket.contact}
        />
      )}

      <ListItem
        button
        dense
        disableGutters
        onClick={(e) => {
          const isCheckboxClicked =
            (e.target.tagName.toLowerCase() === "input" && e.target.type === "checkbox") ||
            (e.target.tagName.toLowerCase() === "svg" && e.target.type === undefined) ||
            (e.target.tagName.toLowerCase() === "path" && e.target.type === undefined);
          if (isCheckboxClicked) return;
          handleSelectTicket(ticket);
        }}
        selected={ticketId && ticketId === ticket.uuid}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
        })}
      >
        {/* ── Avatar ── */}
        <div style={{ flexShrink: 0, marginRight: 10, marginTop: 2 }}>
          <Avatar
            style={{ width: 44, height: 44 }}
            src={ticket?.contact?.urlPicture}
            className={classes.clickableAvatar}
            onClick={handleImageClick}
          />
        </div>

        {/* ── Content ── */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Row 1 — Nome + horário */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
              {ticket.isGroup && ticket.channel === "whatsapp" && (
                <GroupIcon style={{ fontSize: 13, color: grey[500], flexShrink: 0 }} />
              )}
              {ticket.channel && (
                <ConnectionIcon
                  width="13"
                  height="13"
                  connectionType={ticket.channel}
                  style={{ flexShrink: 0 }}
                />
              )}
              <Typography
                noWrap
                component="span"
                style={{
                  fontWeight: 700,
                  fontSize: 13,
                  lineHeight: "18px",
                  color: isDark ? "#f0f0f0" : "#111",
                }}
              >
                {contactDisplayName}
              </Typography>
            </div>
            <span
              style={{
                fontSize: 11,
                lineHeight: "18px",
                flexShrink: 0,
                color: unread > 0 ? "#25D366" : isDark ? "#888" : "#aaa",
                fontWeight: unread > 0 ? 700 : 400,
              }}
            >
              {ticket.updatedAt &&
                (isSameDay(parseISO(ticket.updatedAt), new Date())
                  ? format(parseISO(ticket.updatedAt), "HH:mm")
                  : format(parseISO(ticket.updatedAt), "dd/MM"))}
            </span>
          </div>

          {/* Row 2 — Última mensagem + badge de não-lidas */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
            <Typography
              noWrap
              component="div"
              style={{
                fontSize: 12,
                lineHeight: "16px",
                flex: 1,
                color: unread > 0 ? (isDark ? "#ddd" : "#222") : isDark ? "#777" : "#999",
                fontWeight: unread > 0 ? 600 : 400,
              }}
            >
              {renderLastMessage()}
            </Typography>
            {unread > 0 && (
              <span
                style={{
                  backgroundColor: "#25D366",
                  color: "#fff",
                  borderRadius: 10,
                  minWidth: 18,
                  height: 18,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  flexShrink: 0,
                  padding: "0 4px",
                }}
              >
                {shouldBlurMessages ? "?" : unread > 99 ? "99+" : unread}
              </span>
            )}
          </div>

          {/* Row 3 — Conexão + fila + atendente (metadados operacionais) */}
          <div style={{ display: "flex", gap: 3, marginTop: 5, flexWrap: "wrap", alignItems: "center" }}>
            {ticket.whatsapp && (
              <MetaPill color={connColor}>{ticket.whatsapp.name}</MetaPill>
            )}
            <MetaPill color={ticket.queue?.color || "#9e9e9e"}>
              {ticket.queueId
                ? ticket.queue?.name
                : ticket.status === "lgpd"
                ? "LGPD"
                : i18n.t("momentsUser.noqueue")}
            </MetaPill>
            {ticket.user && (
              <MetaPill color="#424242">{ticket.user.name}</MetaPill>
            )}
          </div>

          {/* Row 4 — Etapas + tags de origem + ações */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 5,
              gap: 4,
            }}
          >
            {/* Tags */}
            <div style={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center", flex: 1, minWidth: 0 }}>
              {stageTags.map((tag) => (
                <StagePill key={`stage-${tag.id}`} color={tag.color}>
                  {tag.name}
                </StagePill>
              ))}
              {originTags.map((tag) => (
                <TagPill key={`tag-${tag.id}`} color={tag.color}>
                  {tag.name}
                </TagPill>
              ))}
            </div>

            {/* Botões de ação */}
            <div style={{ display: "flex", alignItems: "center", flexShrink: 0, gap: 0 }}>
              {/* Chatbot: só espiar */}
              {ticket.status === "chatbot" && (
                <ActionBtn
                  color={isDark ? "#90caf9" : "#1565c0"}
                  tooltip="Espiar conversa do chatbot"
                  loading={loading}
                  onClick={(e) => { e.stopPropagation(); handleSpyTicket(); }}
                >
                  <VisibilityIcon style={{ fontSize: 18 }} />
                </ActionBtn>
              )}

              {ticket.status !== "chatbot" && (
                <>
                  {/* Aceitar (sem fila) */}
                  {ticket.status === "pending" && !ticket.queueId && (
                    <ActionBtn
                      color="#2e7d32"
                      tooltip={i18n.t("ticketsList.buttons.accept")}
                      loading={loading}
                      onClick={(e) => { e.stopPropagation(); handleOpenAcceptTicketWithouSelectQueue(); }}
                    >
                      <Done style={{ fontSize: 18 }} />
                    </ActionBtn>
                  )}
                  {/* Aceitar (com fila) */}
                  {ticket.status === "pending" && ticket.queueId && (
                    <ActionBtn
                      color="#2e7d32"
                      tooltip={i18n.t("ticketsList.buttons.accept")}
                      loading={loading}
                      onClick={(e) => { e.stopPropagation(); handleAcepptTicket(ticket.id); }}
                    >
                      <Done style={{ fontSize: 18 }} />
                    </ActionBtn>
                  )}
                  {/* Transferir */}
                  {["pending", "open", "group"].includes(ticket.status) && (
                    <ActionBtn
                      color="#6a1b9a"
                      tooltip={i18n.t("ticketsList.buttons.transfer")}
                      loading={loading}
                      onClick={(e) => { e.stopPropagation(); handleOpenTransferModal(); }}
                    >
                      <SwapHoriz style={{ fontSize: 18 }} />
                    </ActionBtn>
                  )}
                  {/* Resolver */}
                  {["open", "group"].includes(ticket.status) && (
                    <ActionBtn
                      color="#c62828"
                      tooltip={i18n.t("ticketsList.buttons.closed")}
                      loading={loading}
                      onClick={(e) => { e.stopPropagation(); handleCloseTicket(ticket.id); }}
                    >
                      <HighlightOff style={{ fontSize: 18 }} />
                    </ActionBtn>
                  )}
                  {/* Ignorar/fechar pending */}
                  {["pending", "lgpd"].includes(ticket.status) &&
                    (user.userClosePendingTicket === "enabled" || user.profile === "admin") && (
                      <ActionBtn
                        color="#c62828"
                        tooltip={i18n.t("ticketsList.buttons.ignore")}
                        loading={loading}
                        onClick={(e) => { e.stopPropagation(); handleCloseIgnoreTicket(ticket.id); }}
                      >
                        <HighlightOff style={{ fontSize: 18 }} />
                      </ActionBtn>
                    )}
                  {/* Novo ticket (fechado) */}
                  {ticket.status === "closed" && (
                    <ActionBtn
                      color="#2e7d32"
                      tooltip="Criar Novo Ticket"
                      loading={loading}
                      onClick={(e) => { e.stopPropagation(); handleOpenNewTicketModal(); }}
                    >
                      <Add style={{ fontSize: 18 }} />
                    </ActionBtn>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </ListItem>

      {/* Modal de Finalização de Venda */}
      {openFinalizacaoVenda && (
        <FinalizacaoVendaModal
          open={openFinalizacaoVenda}
          onClose={() => setOpenFinalizacaoVenda(false)}
          ticket={ticket}
          onFinalizar={(ticketData) => {
            setOpenFinalizacaoVenda(false);
            setTicketDataToFinalize(ticketData);
            setShowFinalizacaoOptions(true);
          }}
        />
      )}

      {/* Modal de Opções de Finalização */}
      {showFinalizacaoOptions && (
        <Dialog
          open={showFinalizacaoOptions}
          onClose={() => setShowFinalizacaoOptions(false)}
          aria-labelledby="finalizacao-options-title"
        >
          <DialogActions>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(ticketDataToFinalize, false, null);
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingWithoutFarewellMsg")}
            </Button>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(ticketDataToFinalize, true, null);
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingCancel")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de imagem do avatar */}
      <Dialog
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <img
            src={ticket?.contact?.urlPicture}
            alt={ticket?.contact?.name || "Foto do contato"}
            className={classes.expandedImage}
          />
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default TicketListItemCustom;
